import { useTranslation } from '@wix/wix-i18n-config';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useStore } from 'react-redux';

import { MESSAGES } from '../../../../constants';
import { TLocalesKeys } from '../../../../types';
import { logChatbotDisplayWarning } from '../../../bi';
import { useIsAiExperience } from '../../../common/hooks/useIsAiExperience';
import { IBotTyping } from '../../../common/messageCreators';
import { selectItems } from '../../../common/selectors/chat';
import { BotAvatarAI } from '../BotAvatar/BotAvatarAI';
import { MessageItem } from '../MessageItem';

const WARNING_TIMEOUT = 6; // seconds
const EXTRA_WARNING_TIMEOUT = 12; // seconds

export const BotTypingAI: FC<IBotTyping> = ({ id }) => {
  const store = useStore();
  const items = useSelector(selectItems);
  const [showMessageWarning, setShowMessageWarning] = useState(false);
  const [showMessageExtraWarning, setShowMessageExtraWarning] = useState(false);
  const { t } = useTranslation();

  const isAiExperience = useIsAiExperience();

  const messageWarning = useMemo(() => {
    if (!isAiExperience || (items.length === 1 && items[0].type === MESSAGES.BOT_TYPING)) {
      // Even if it is AI experience but conversation is empty (just have the single bot-typing message)
      // we want to see the default message
      return t<TLocalesKeys>('bot_response_takes_too_long');
    }

    if (showMessageExtraWarning) {
      return t<TLocalesKeys>('bot_response_ai_takes_too_long_extra_warning');
    }

    if (showMessageWarning) {
      return t<TLocalesKeys>('bot_response_ai_takes_too_long');
    }

    return t<TLocalesKeys>('bot_response_ai_default');
  }, [t, showMessageWarning, showMessageExtraWarning, isAiExperience, items]);

  const logBiEvent = useCallback(
    (timeWaited: number, messageLocalKey: TLocalesKeys) => {
      logChatbotDisplayWarning(store.getState(), {
        timeWaited,
        messageText: t<TLocalesKeys>(messageLocalKey),
        messageId: id,
      });
    },
    [t, id, store],
  );

  useEffect(() => {
    const warningTimer = setTimeout(() => {
      setShowMessageWarning(true);
      logBiEvent(WARNING_TIMEOUT, 'bot_response_ai_takes_too_long');
    }, WARNING_TIMEOUT * 1000);

    const extraWarningTimer = setTimeout(() => {
      setShowMessageExtraWarning(true);
      logBiEvent(EXTRA_WARNING_TIMEOUT, 'bot_response_ai_takes_too_long_extra_warning');
    }, EXTRA_WARNING_TIMEOUT * 1000);

    return () => {
      clearTimeout(warningTimer);
      clearTimeout(extraWarningTimer);
    };
  }, [logBiEvent]);

  return (
    <MessageItem id={id} dataHook="loading-item">
      <BotAvatarAI isLoading={true} text={messageWarning} />
    </MessageItem>
  );
};
